import React, { useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useGetSharedPublicPostQuery } from '../../api/client';
import ShareMapFullscreen from './components/ShareMapFullscreen';
import ShareDrawer from './components/ShareDrawer';
import AppBanner from './components/AppBanner';
import '../../App.css';

export default function SharePostPage(): React.ReactElement {
  const { shareId } = useParams<{ shareId: string }>();
  const [isDrawerOpen, setIsDrawerOpen] = useState(true); // Open by default

  const {
    data: post,
    isLoading,
    error
  } = useGetSharedPublicPostQuery(shareId || '', {
    skip: !shareId
  });

  const handleMarkerClick = useCallback(() => {
    setIsDrawerOpen(true);
  }, []);

  const handleDrawerMinimize = useCallback(() => {
    // Don't fully close, just minimize the drawer
    // We'll implement this with CSS to show just the handle
    setIsDrawerOpen(false);
  }, []);

  // Memoize the map component to prevent re-renders when drawer state changes
  const memoizedMap = useMemo(() => {
    if (!post) return null;
    return <ShareMapFullscreen post={post} onMarkerClick={handleMarkerClick} />;
  }, [post, handleMarkerClick]);

  if (!shareId) {
    return (
      <div className="share-page error-state">
        <h1>Invalid Post Link</h1>
        <p>The post link appears to be invalid.</p>
        <a href="/download">Download the Hereabout App</a>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="share-page loading-state">
        <div className="loading-spinner">Loading post...</div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="share-page error-state">
        <h1>Post Not Found</h1>
        <p>This post may be private or no longer available.</p>
        <a href="/download">Download the Hereabout App</a>
      </div>
    );
  }

  return (
    <div className="share-page-fullscreen">
      {/* App Banner */}
      <AppBanner postId={post.id} />

      {/* Fullscreen Map */}
      {memoizedMap}

      {/* Bottom Drawer */}
      <ShareDrawer
        post={post}
        isOpen={isDrawerOpen}
        onMinimize={handleDrawerMinimize}
        onExpand={handleMarkerClick}
      />
    </div>
  );
}
